import { createContext, useContext } from 'react';
import { Helmet } from 'react-helmet';

// When viewing a React blog, Blog.jsx provides index metadata (dates, keywords) for SEO
export const BlogMetadataContext = createContext(null);

// Helper to generate responsive srcset for Unsplash images
// Aggressive mobile optimization for faster LCP
function getResponsiveSrcSet(url) {
  if (!url || !url.includes('unsplash.com')) {
    return null;
  }
  
  // Smaller sizes for mobile: 400px phones, 600px large phones, 900px tablets, 1200px desktop
  const widths = [400, 600, 900, 1200];
  return widths
    .map(w => {
      const urlObj = new URL(url);
      urlObj.searchParams.set('w', w.toString());
      urlObj.searchParams.set('q', '80');
      urlObj.searchParams.set('auto', 'format');
      urlObj.searchParams.set('fit', 'crop');
      return `${urlObj.toString()} ${w}w`;
    })
    .join(', ');
}

// Build Article/BlogPosting schema for SEO and AI crawlers when meta has dates
function getArticleStructuredData(meta) {
  const { title, summary, hero, url, type, createdAt, updatedAt } = meta;
  const author = meta.author || 'Reserve Nature';
  if (type !== 'article' || !url) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: summary,
    image: hero,
    url,
    author: {
      '@type': 'Organization',
      name: author,
      url: 'https://reservenature.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Reserve Nature',
      url: 'https://reservenature.com',
      logo: { '@type': 'ImageObject', url: 'https://reservenature.com/reserve-dark.png' },
    },
    ...(createdAt && { datePublished: createdAt }),
    ...(updatedAt && { dateModified: updatedAt }),
  };
}

export default function MetaInfo({ children, meta, structuredData, preloadHero = false }) {
  const indexMeta = useContext(BlogMetadataContext);
  const {
    title,
    summary,
    hero = 'https://reservenature.com/public/reserve-dark.png',
    url,
    type,
    keywords: metaKeywords,
    createdAt: metaCreatedAt,
    updatedAt: metaUpdatedAt,
  } = meta;
  const keywords = metaKeywords || indexMeta?.keywords || (Array.isArray(indexMeta?.tags) ? indexMeta.tags.slice(0, 12).join(', ') : undefined);
  const createdAt = metaCreatedAt || indexMeta?.createdAt;
  const updatedAt = metaUpdatedAt || indexMeta?.updatedAt;
  
  // Generate responsive srcset for preloading
  const heroSrcSet = preloadHero ? getResponsiveSrcSet(hero) : null;
  // Match BlogHero sizes: small phones 400px, phones 600px, tablets 900px, desktop 1200px
  const heroSizes = '(max-width: 480px) 400px, (max-width: 768px) 600px, (max-width: 1024px) 900px, 1200px';
  const articleSchema = getArticleStructuredData({ ...meta, createdAt, updatedAt });
  
  return (
    <Helmet>
      {children}
      <link rel="canonical" href={url} />
      {/* Preload hero image for LCP optimization - responsive for mobile */}
      {preloadHero && hero && heroSrcSet && (
        <link 
          rel="preload" 
          as="image" 
          href={hero}
          imagesrcset={heroSrcSet}
          imagesizes={heroSizes}
          fetchpriority="high" 
        />
      )}
      {/* Fallback for non-Unsplash images */}
      {preloadHero && hero && !heroSrcSet && (
        <link rel="preload" as="image" href={hero} fetchpriority="high" />
      )}
      <title>{title}</title>
      <meta property="og:title" content={title} />
      <meta name="twitter:title" content={title}></meta>

      <meta name="description" content={summary} />
      <meta property="og:description" content={summary} />
      <meta name="twitter:description" content={summary} />

      <meta property="og:image" content={hero} />
      <meta name="twitter:image" content={hero} />

      <meta property="og:url" content={url} />
      <meta name="twitter:url" content={url}></meta>

      <meta property="og:type" content={type} />

      {/* Site name for social platforms */}
      <meta property="og:site_name" content="Reserve Nature" />

      {/* SEO keywords for search engines */}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Article metadata for freshness signals */}
      {createdAt && <meta property="article:published_time" content={createdAt} />}
      {updatedAt && <meta property="article:modified_time" content={updatedAt} />}

      {/* Twitter card type for better previews */}
      <meta name="twitter:card" content="summary_large_image" />

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      {/* Article schema for search engines and AI crawlers */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
}
